import { DialogProps } from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React from "react";
import { ModalId, ModalType } from "../consts/Modal";
import classNames from "classnames/bind";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./ModalManager.module.scss";
import { createCleanroomForSummary } from "../../srcV2/pages/CleanroomCreateInstall/utils";
import CleanroomManager from "../../srcV2/common/managers/CleanroomManager/CleanroomManager";

const cx = classNames.bind(styles);

const Profile = React.lazy(
    () => import("../../common/components/molecules/Profile/ProfileModal"),
);
const ActivationHub = React.lazy(
    () => import("../../pages/Analysis/ActivationHub/ActivationHub"),
);
const ActivationHubV2 = React.lazy(
    () =>
        import(
            "../../srcV2/pages/AnalysisPage/CommonComponents/ActivationHub/ActivationHub"
        ),
);
const SummaryCard = React.lazy(
    () => import("../../common/components/molecules/Summary/Summary"),
);
const SummaryCardV2 = React.lazy(
    () => import("../../srcV2/components/elements/Summary/Summary"),
);
const SaveAnalysis = React.lazy(
    () =>
        import(
            "../../common/components/molecules/SaveAnalysisModal/SaveAnalysisModal"
        ),
);
const ChooseAnalysis = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ChooseAnalysis/ChooseAnalysis"
        ),
);
const ChooseAnalysisV2 = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ChooseAnalysis/ChooseAnalysisV2"
        ),
);
const UpdatesInProgress = React.lazy(
    () =>
        import(
            "../../common/components/molecules/UpdatesInProgress/UpdatesInProgress"
        ),
);
const ProviderRunEnabled = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ProviderRunEnabled/ProviderRunEnabled"
        ),
);
const ImpactToEnabledTemplates = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ImpactToEnabledTemplates/ImpactToEnabledTemplates"
        ),
);
const ImpactToDeletedObjects = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ImpactToDeletedObjects/ImpactToDeletedObjects"
        ),
);
const ImpactToDeletedObjectsOnRunAnalysis = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ImpactToDeletedObjectsOnRunAnalysis/ImpactToDeletedObjectsOnRunAnalysis"
        ),
);
const InternalTestEnabled = React.lazy(
    () =>
        import(
            "../../common/components/molecules/InternalTestEnabled/InternalTestEnabled"
        ),
);
const NoActiveCollaborators = React.lazy(
    () =>
        import(
            "../../common/components/molecules/NoActiveCollaborators/NoActiveCollaborators"
        ),
);
const AddManagedAccount = React.lazy(
    () =>
        import(
            "../../common/components/molecules/AddManagedAccount/AddManagedAccount"
        ),
);
const AddSnowflakePartnersAccount = React.lazy(
    () =>
        import(
            "../../common/components/molecules/AddSnowflakePartnersAccount/AddSnowflakePartnersAccount"
        ),
);
const AddUser = React.lazy(
    () => import("../../common/components/molecules/AddUser/AddUser"),
);
const AddUserConfirmation = React.lazy(
    () =>
        import(
            "../../common/components/molecules/AddUserConfirmation/AddUserConfirmation"
        ),
);
const EditUser = React.lazy(
    () => import("../../common/components/molecules/EditUser/EditUser"),
);
const EditCleanroomFeatures = React.lazy(
    () =>
        import(
            "../../common/components/molecules/EditCleanroomFeatures/EditCleanroomFeatures"
        ),
);
const AddRampIdPartner = React.lazy(
    () =>
        import(
            "../../common/components/molecules/AddRampIdPartner/AddRampIdPartner"
        ),
);
const RefreshLiveramp = React.lazy(
    () =>
        import(
            "../../common/components/molecules/RefreshLiveramp/RefreshLiveramp"
        ),
);
const EnableCrossCloudConfirmation = React.lazy(
    () =>
        import(
            "../../common/components/molecules/EnableCrossCloudConfirmation/EnableCrossCloudConfirmation"
        ),
);
const WelcomeExperience = React.lazy(
    () =>
        import(
            "../../common/components/molecules/WelcomeExperience/WelcomeExperience"
        ),
);
const Prompt = React.lazy(
    () => import("../../common/components/molecules/Prompt/Prompt"),
);
const Notification = React.lazy(
    () => import("../../common/components/molecules/Notification/Notification"),
);
const ErrorModal = React.lazy(
    () => import("../../common/components/molecules/ErrorModal/ErrorModal"),
);
const LoaderModal = React.lazy(
    () => import("../../common/components/molecules/LoaderModal/LoaderModal"),
);
const AddressColumns = React.lazy(
    () =>
        import(
            "../../common/components/molecules/AddressColumns/AddressColumns"
        ),
);
const CleanroomJoinWarning = React.lazy(
    () =>
        import(
            "../../common/components/molecules/CleanroomJoinWarning/CleanroomJoinWarning"
        ),
);
const ServiceAccountAuthUpdate = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ServiceAccountAuthUpdate/ServiceAccountAuthUpdate"
        ),
);
const ImageCropper = React.lazy(
    () => import("../../common/components/molecules/ImageCropper/ImageCropper"),
);
const FreeformSQLInfoModal = React.lazy(
    () =>
        import(
            "../../common/components/molecules/FreeformSQLInfoModal/FreeformSQLInfoModal"
        ),
);
const ConfirmSnowflakeAuthModal = React.lazy(
    () =>
        import(
            "../../common/components/molecules/ConfirmSnowflakeAuthModal/ConfirmSnowflakeAuthModal"
        ),
);
const UserAlteredAdminModal = React.lazy(
    () =>
        import(
            "../../common/components/molecules/UserAlteredAdminModal/UserAlteredAdminModal"
        ),
);
const SnowflakeErrorModal = React.lazy(
    () =>
        import(
            "../../common/components/molecules/SnowflakeErrorModal/SnowflakeErrorModal"
        ),
);
const RequestAdditionalInvitesModal = React.lazy(
    () =>
        import(
            "../../common/components/molecules/RequestAdditionalInvitesModal/RequestAdditionalInvitesModal"
        ),
);
const CollaborationUpgradeModal = React.lazy(
    () =>
        import(
            "../../common/components/molecules/CollaborationUpgradeModal/CollaborationUpgradeModal"
        ),
);

/**
 * Get Modal Details
 * @returns
 */
export function getModalDetails({ type, props, onAction, onClose }) {
    let dialogProps = {
        fullWidth: true,
        maxWidth: "md",
    } as Partial<DialogProps>;
    let content = null;

    if (type === ModalType.Custom) {
        content = getCustomModalContent({ props, onAction, onClose });
        dialogProps = getCustomModalDialogProps(props);
    } else if (type === ModalType.Confirm) {
        const { title, key = "Prompt", ...promptProps } = props;
        content = (
            <>
                <DialogTitle id="prompt">
                    {title}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Prompt
                        key={key}
                        {...promptProps}
                        onAction={onAction}
                        onClose={onClose}
                    />
                </DialogContent>
            </>
        );
        dialogProps = {
            fullWidth: promptProps?.fullWidth ? promptProps?.fullWidth : true,
            maxWidth: promptProps?.maxWidth ? promptProps?.maxWidth : "xs",
        };
    } else if (type === ModalType.Notification) {
        const { key = "Notification", ...notiticationProps } = props;
        content = (
            <>
                <DialogTitle id="notitication">
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Notification
                        key={key}
                        {...notiticationProps}
                        onAction={onAction}
                        onClose={onClose}
                    />
                </DialogContent>
            </>
        );
        dialogProps = {
            fullWidth: true,
            maxWidth: "sm",
        };
    }

    return {
        dialogProps,
        content,
    };
}

export function getCustomModalDialogProps(props): Partial<DialogProps> {
    const { id } = props;
    switch (id) {
        case ModalId.TRIAL_REMAINING:
        case ModalId.LoaderModal:
            return {
                fullWidth: false,
                maxWidth: "sm",
            };
        case ModalId.AddRampIdPartner:
        case ModalId.ChooseAnalysis:
        case ModalId.ChooseAnalysisV2:
        case ModalId.EditUser:
        case ModalId.RefreshLiveramp:
        case ModalId.AddUserConfirmation:
        case ModalId.Error:
        case ModalId.ServiceAccountAuthUpdate:
        case ModalId.ImageCropper:
        case ModalId.UpdatesInProgress:
        case ModalId.ImpactToEnabledTemplates:
        case ModalId.ImpactToDeletedObjects:
        case ModalId.ImpactToDeletedObjectsOnRunAnalysis:
        case ModalId.InternalTestEnabled:
        case ModalId.UserAlteredAdminModal:
        case ModalId.RequestAdditionalInvites:
            return {
                fullWidth: true,
                maxWidth: "sm",
            };
        case ModalId.EditAccount:
        case ModalId.AddAccount:
        case ModalId.AddUser:
        case ModalId.Profile:
        case ModalId.EnableCrossCloudConfirmation:
        case ModalId.ProviderRunEnabled:
        case ModalId.SaveAnalysis:
        case ModalId.CleanroomSummary:
        case ModalId.CleanroomSummaryV2:
        case ModalId.CleanroomJoinWarning:
        case ModalId.SnowflakeErrorModal:
        case ModalId.NoActiveCollaborators:
            return {
                fullWidth: true,
                maxWidth: "md",
            };
        case ModalId.EditSnowflakePartnersAccount:
        case ModalId.AddSnowflakePartnersAccount:
        case ModalId.AddressColumns:
            return {
                maxWidth: "lg",
                fullWidth: true,
            };
        case ModalId.CollaborationUpgrade:
            return {
                fullWidth: false,
                maxWidth: "lg",
                PaperProps: {
                    sx: {
                        width: "896px",
                        maxWidth: "896px",
                        borderRadius: "16px",
                        boxShadow: "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    },
                },
            } satisfies Partial<DialogProps>;
        case ModalId.WelcomeExperience:
            return {
                maxWidth: "lg",
            };

        default: {
            return {
                fullWidth: true,
                maxWidth: "md",
            };
        }
    }
}

/**
 * Get Custom Modal Content
 * @returns
 */
export function getCustomModalContent({ props, onAction, onClose }) {
    const { id, ...otherProps } = props;
    switch (id) {
        case ModalId.Profile: {
            const { title, key = "Profile", ...profileProps } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <Profile
                            key={key}
                            {...profileProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }

        case ModalId.ActivationHub: {
            return (
                <>
                    <DialogTitle id={id}>Activation Hub</DialogTitle>
                    <DialogContent>
                        <ActivationHub
                            {...otherProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }

        case ModalId.ActivationHubV2: {
            return (
                <>
                    <DialogTitle id={id}>Activation Hub</DialogTitle>
                    <DialogContent>
                        <ActivationHubV2
                            {...otherProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }

        case ModalId.SaveAnalysis: {
            const {
                title,
                key = "SaveAnalysis",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <SaveAnalysis
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }

        case ModalId.ChooseAnalysis: {
            const {
                title,
                key = "ChooseAnalysis",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <ChooseAnalysis
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ChooseAnalysisV2: {
            const {
                title,
                key = "ChooseAnalysisV2",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <ChooseAnalysisV2
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.UpdatesInProgress: {
            const {
                title,
                key = "UpdatesInProgress",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <UpdatesInProgress
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ProviderRunEnabled: {
            const {
                title,
                key = "ProviderRunEnabled",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <ProviderRunEnabled
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ImpactToEnabledTemplates: {
            const {
                title,
                key = "ImpactToEnabledTemplates",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <ImpactToEnabledTemplates
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ImpactToDeletedObjects: {
            const {
                title,
                key = "ImpactToDeletedObjects",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent style={{ padding: 0 }}>
                        <ImpactToDeletedObjects
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ImpactToDeletedObjectsOnRunAnalysis: {
            const {
                title,
                key = "ImpactToDeletedObjectsOnRunAnalysis",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent style={{ padding: 0 }}>
                        <ImpactToDeletedObjectsOnRunAnalysis
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.InternalTestEnabled: {
            const {
                title,
                key = "InternalTestEnabled",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <InternalTestEnabled
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.NoActiveCollaborators: {
            const {
                title,
                key = "NoActiveCollaborators",
                ...analysisProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id} style={{ paddingTop: "30px" }}>
                        {title}
                    </DialogTitle>
                    <DialogContent style={{ padding: "0 24px 8px" }}>
                        <NoActiveCollaborators
                            key={key}
                            {...analysisProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ServiceAccountAuthUpdate: {
            const {
                title,
                key = "ServiceAccountAuthUpdate",
                ...serviceAccountAuthUpdateProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <ServiceAccountAuthUpdate
                            key={key}
                            {...serviceAccountAuthUpdateProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.CleanroomSummary:
            const { summary } = otherProps;
            const modifiedSummary = {
                ...summary,
                show_secure_message: false,
            };
            return (
                <>
                    <DialogContent className={cx("CleanroomSummaryDetails")}>
                        <SummaryCard
                            summaries={[modifiedSummary]}
                            onResize={() => {}}
                            inModal
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        case ModalId.CleanroomSummaryV2:
            const tabContentsContainerStyle: React.CSSProperties = {
                overflowY: "auto",
                maxHeight: "800px",
                height: "800px",
                display: "flex",
            };

            const { cleanroom } = otherProps;
            const isCleanroomProvider = CleanroomManager.isCleanroomProvider({
                cleanroom,
            });
            const cleanroomForSummary = createCleanroomForSummary(
                cleanroom,
                isCleanroomProvider,
            );

            return (
                <>
                    <DialogContent className={cx("CleanroomSummaryDetails")}>
                        <SummaryCardV2
                            {...otherProps}
                            cleanroom={cleanroomForSummary}
                            tabContentsContainerStyle={
                                tabContentsContainerStyle
                            }
                            isCollapsible={false}

                            // onResize={() => {}}
                            // inModal
                            // onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        case ModalId.AddAccount: {
            const {
                title,
                key = "AddAccount",
                ...AddAccountsProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddManagedAccount
                            key={key}
                            {...AddAccountsProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.AddSnowflakePartnersAccount: {
            const {
                title,
                key = "AddSnowflakePartnersAccount",
                ...AddSnowflakePartnersAccountProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddSnowflakePartnersAccount
                            key={key}
                            {...AddSnowflakePartnersAccountProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.EditSnowflakePartnersAccount: {
            const {
                title,
                key = "EditSnowflakePartnersAccount",
                ...EditSnowflakePartnersAccountProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddSnowflakePartnersAccount
                            key={key}
                            {...EditSnowflakePartnersAccountProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.EditAccount: {
            const {
                title,
                key = "EditAccount",
                ...EditAccountProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddManagedAccount
                            key={key}
                            {...EditAccountProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.AddUser: {
            const { title, key = "AddUser", ...addUserProps } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddUser
                            key={key}
                            {...addUserProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.AddUserConfirmation: {
            const { title, key = "AddUser", ...addUserProps } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddUserConfirmation
                            key={key}
                            {...addUserProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.EditUser: {
            const { title, key = "EditUser", ...editUserProps } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <EditUser
                            key={key}
                            {...editUserProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }

        case ModalId.AddRampIdPartner: {
            const {
                title,
                key = "AddRampIdPartner",
                ...addRampIdPartnerProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddRampIdPartner
                            key={key}
                            {...addRampIdPartnerProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.EditCleanroomFeatures: {
            const {
                title,
                key = "EditCleanroomFeatures",
                ...EditCleanroomFeaturesProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <EditCleanroomFeatures
                            key={key}
                            {...EditCleanroomFeaturesProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.RefreshLiveramp: {
            const {
                title,
                key = "RefreshLiveramp",
                ...refreshLiverampProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <RefreshLiveramp
                            key={key}
                            {...refreshLiverampProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.Error: {
            const { key = "ErrorModal", ...errorProps } = otherProps;
            return (
                <>
                    <DialogContent>
                        <ErrorModal
                            key={key}
                            {...errorProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.LoaderModal: {
            const { key = "LoaderModal", ...loaderModalProps } = otherProps;
            return (
                <>
                    <DialogContent>
                        <LoaderModal
                            key={key}
                            {...loaderModalProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.AddressColumns: {
            const {
                title,
                key = "AddressColumns",
                ...addressColumnsProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <AddressColumns
                            key={key}
                            {...addressColumnsProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ImageCropper: {
            const {
                title,
                key = "ImageCropper",
                ...imageCropperProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <ImageCropper
                            key={key}
                            {...imageCropperProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.CleanroomJoinWarning: {
            const {
                title,
                key = "CleanroomJoinWarning",
                ...CleanroomJoinWarningProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <CleanroomJoinWarning
                            key={key}
                            {...CleanroomJoinWarningProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.EnableCrossCloudConfirmation: {
            const {
                title,
                key = "EnableCrossCloudConfirmation",
                ...EnableCrossCloudConfirmationProps
            } = otherProps;
            return (
                <>
                    <DialogTitle id={id}>{title}</DialogTitle>
                    <DialogContent>
                        <EnableCrossCloudConfirmation
                            key={key}
                            {...EnableCrossCloudConfirmationProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.WelcomeExperience: {
            const {
                title,
                key = "WelcomeExperience",
                ...WelcomeExperienceProps
            } = otherProps;
            return (
                <>
                    <DialogContent style={{ padding: 0 }}>
                        <WelcomeExperience
                            key={key}
                            {...WelcomeExperienceProps}
                            onAction={onAction}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.ConfirmSnowflakeAuthModal: {
            const {
                key = "ConfirmSnowflakeAuthModal",
                ...ConfirmSnowflakeAuthModalProps
            } = otherProps;
            return (
                <>
                    <DialogContent>
                        <ConfirmSnowflakeAuthModal
                            key={key}
                            {...ConfirmSnowflakeAuthModalProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.FreeformSQLInfoModal: {
            const {
                title,
                key = "FreeformSQLInfoModal",
                ...FreeformSQLInfoModalProps
            } = otherProps;
            return (
                <>
                    <DialogContent style={{ padding: 0 }}>
                        <FreeformSQLInfoModal
                            key={key}
                            {...FreeformSQLInfoModalProps}
                            onAction={onAction}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.UserAlteredAdminModal: {
            const {
                key = "UserAlteredAdminModal",
                ...UserAlteredAdminModalProps
            } = otherProps;
            return (
                <>
                    <DialogContent>
                        <UserAlteredAdminModal
                            key={key}
                            {...UserAlteredAdminModalProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.SnowflakeErrorModal: {
            const { key = "SnowflakeErrorModal", ...SnowflakeErrorModalProps } =
                otherProps;
            return (
                <>
                    <DialogContent>
                        <SnowflakeErrorModal
                            key={key}
                            {...SnowflakeErrorModalProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.RequestAdditionalInvites: {
            const {
                key = "RequestAdditionalInvitesModal",
                ...requestAdditionalProps
            } = otherProps;
            return (
                <>
                    <DialogContent>
                        <RequestAdditionalInvitesModal
                            key={key}
                            {...requestAdditionalProps}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        case ModalId.CollaborationUpgrade: {
            const {
                key = "CollaborationUpgradeModal",
                ...collaborationUpgradeProps
            } = otherProps;
            return (
                <>
                    <DialogContent style={{ padding: 0 }}>
                        <CollaborationUpgradeModal
                            key={key}
                            {...collaborationUpgradeProps}
                            onAction={onAction}
                            onClose={onClose}
                        />
                    </DialogContent>
                </>
            );
        }
        default: {
            return null;
        }
    }
}
